import axios from '../axiosConfig';
import { formatDateTime } from '../lib/utils';
import type { Appendix, CodeItemAttributeSchemaScope, CodeItemGroup, CodeItemSubgroup, IfNode, RuleNode, UnselectableValuesReport } from '../types/codeIndex';
import { normalizeAttributeSchemaScopes, normalizeAttributeValues } from '../utils/codeItemAttributes';
import { normalizeAppendixRefs, normalizeGroupAppendixRefs } from '../utils/appendixRefs';
import { deriveCompositeSubgroups } from '../utils/compositeCodeUtils';
import { showConfirmModal, showSuccessModal, showErrorModal, showLoadingModal, closeLoadingModal } from '../components/ui/ConfirmDialog';
import { getApiMessage } from '../utils/apiMessage';

// -----------------------------
// Code Sheet - Details (CodeItems)
// -----------------------------
type CodeItemsApiRow = {
  id: string | null;
  rowId: string;
  optionCode: string;
  status?: string | null;
  group?: string | null;
  valuePosition?: number | null;
  valuePositionEnd?: number | null;
  position?: number | null;
  description: string | null;
  descriptionBlockId: string;
  availability: null;
  availabilityBlockId: string | null;
  rule?: any | null;
  appendix?: any | null;
  appendixRefs?: string[] | null;
  attributeValues?: Record<string, string | null> | null;
  regions?: string[] | null;
};
export type CodeItemsApiGroup = {
  id: string | null;
  code: string;
  posRange: number[];
  isDefault: boolean;
  title?: string | null;
  attributeSchemas?: CodeItemAttributeSchemaScope[] | null;
  appendixRefs?: string[] | null;
  groupAppendixRefs?: Record<string, string[]> | null;
  rows: CodeItemsApiRow[];
  // Alternative API structure fields
  codeItem?: number;
  position?: number;
  len?: number;
  name?: string;
  mode?: string;
  desc?: string;
  regions?: string[] | null;
  appendix?: any | null;
  formatValues?: Array<{
    name?: string;
    value?: string;
    status?: string | null;
    group?: string | null;
    valuePosition?: number | null;
    valuePositionEnd?: number | null;
    position?: number | null;
    desc?: string;
    descriptionBlockId?: string;
    availabilityBlockId?: string | null;
    codeItemId?: string | null;
    appendix?: any | null;
    appendixRefs?: string[] | null;
    attributeValues?: Record<string, string | null> | null;
    rule?: {
      description?: string | null;
      appendix?: {
        title?: string;
        description?: string;
        content?: string;
        id?: string;
      } | null;
      id?: string;
    } | null;
    regions?: string[] | null;
    id?: string;
  }>;
};

// Rule types for availability rules
export interface CodeItemsLoopNode {
  type: 'while';
  reference: string;
  condition: string;
  value: string;
  id?: string | null;
  description?: string | null;
  appendix?: { id?: string | null; title?: string | null; description?: string | null; content?: string | null } | null;
}

export interface CodeItemsConditionEntry {
  reference: string;
  condition: string;
  value?: string | null;
  from?: string | null;
  to?: string | null;
  values?: Record<string, string> | null;
}

export interface CodeItemsConditionGroup {
  logicalOperator: 'AND' | 'OR';
  conditions?: CodeItemsConditionEntry[] | null;
  groups?: CodeItemsConditionGroup[] | null;
}

export interface CodeItemsIfNode {
  type: 'if';
  reference?: string | null;
  condition?: string | null;
  value?: string | null;
  returnValue?: string | null;
  future?: boolean | null;
  conditionGroup?: CodeItemsConditionGroup | null;
  consequenceGroups?: CodeItemsConditionGroup[] | null;
  if?: CodeItemsIfNode | null;
  else?: CodeItemsIfNode | null;
  id?: string | null;
  description?: string | null;
  appendix?: { id?: string | null; title?: string | null; description?: string | null; content?: string | null } | null;
}

export interface CodeItemsTextNode {
  type: 'text';
  id?: string | null;
  description?: string | null;
  appendix?: { id?: string | null; title?: string | null; description?: string | null; content?: string | null } | null;
  freeText?: string | null;
}

export type CodeItemsRuleNode = CodeItemsLoopNode | CodeItemsIfNode | CodeItemsTextNode;

export type CodeItemsRow = {
  id?: string | null;
  rowId: string;
  optionCode: string;
  status?: string | null;
  group?: string | null;
  valuePosition?: number | null;
  valuePositionEnd?: number | null;
  position: number;
  description: string | null;
  descriptionBlockId: string;
  availabilityBlockId: string;
  regions?: string[];
  isNew?: boolean;
  rule?: CodeItemsRuleNode | null;
  attributeValues?: Record<string, string | null>;
  appendixRefs?: string[];
  appendix?: {
    id?: string | null;
    title?: string | null;
    description?: string | null;
    content?: string | null;
    attachment?: string | null;
    entityId?: string | null;
    parentEntityId?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    tables?: unknown[] | null;
  } | null;
};

export type CodeItemsGroup = {
  id?: string | null;
  localKey?: string;
  codeItemPos: { code: string; posRange: [number, number]; label: string; isDefault: boolean };
  title?: string | null;
  attributeSchemas?: CodeItemAttributeSchemaScope[];
  authoringMode?: 'simple' | 'composite';
  subgroups?: CodeItemSubgroup[];
  appendix?: Appendix | null;
  appendixRefs?: string[];
  groupAppendixRefs?: Record<string, string[]>;
  rows: CodeItemsRow[];
};

function normalizeCodeRowStatus(value: unknown): string {
  const normalizedValue = String(value ?? '').trim().toUpperCase();
  return normalizedValue === 'EMPTY' ? '' : normalizedValue;
}

function makeCodeItemLocalKey(code: string, start: number, apiId: string | null | undefined, index: number): string {
  const normalizedApiId = String(apiId || '').trim();
  if (normalizedApiId) return `ci:${normalizedApiId}`;

  return `ci:${code}:${start}:${index}`;
}

function mapAppendixFromApi(apiAppendix: any) {
  if (!apiAppendix) return null;

  return {
    id: apiAppendix.id || null,
    title: apiAppendix.title || null,
    description: apiAppendix.description || null,
    content: apiAppendix.content || null,
    attachment: apiAppendix.attachment || null,
    entityId: apiAppendix.entityId || apiAppendix.entity_id || null,
    parentEntityId: apiAppendix.parentEntityId || apiAppendix.parent_entity_id || null,
    createdAt: apiAppendix.createdAt || apiAppendix.created_at || null,
    updatedAt: apiAppendix.updatedAt || apiAppendix.updated_at || null,
    // The sheet save reconciles appendix tables, so tables read here must be sent back as-is.
    ...(Array.isArray(apiAppendix.tables) ? { tables: apiAppendix.tables } : {})
  };
}

function mapConditionGroupFromApi(apiGroup: any): CodeItemsConditionGroup | null {
  if (!apiGroup) return null;

  const logicalOperator = apiGroup.logicalOperator === 'AND' || apiGroup.logicalOperator === 'OR'
    ? apiGroup.logicalOperator
    : null;

  if (!logicalOperator) return null;

  const conditions = Array.isArray(apiGroup.conditions)
    ? apiGroup.conditions
      .filter((c: any) => c && (c.reference || c.condition || c.value || c.from || c.to || c.values))
      .map((c: any) => ({
        reference: c.reference ?? '',
        condition: c.condition ?? '',
        value: c.value ?? null,
        from: c.from ?? null,
        to: c.to ?? null,
        values: c.values && typeof c.values === 'object' && !Array.isArray(c.values)
          ? Object.fromEntries(Object.entries(c.values).map(([key, value]) => [key, String(value ?? '')]))
          : null,
      }))
    : null;

  const groups = Array.isArray(apiGroup.groups)
    ? apiGroup.groups
      .map((g: any) => mapConditionGroupFromApi(g))
      .filter((g: CodeItemsConditionGroup | null): g is CodeItemsConditionGroup => Boolean(g))
    : null;

  return {
    logicalOperator,
    conditions,
    groups
  };
}

function extractEmbeddedFreeText(description: string | null | undefined): string | null {
  const normalizedDescription = String(description || '').trim();
  if (!normalizedDescription) return null;

  const separatorIndex = normalizedDescription.lastIndexOf(';');
  if (separatorIndex === -1) return null;

  const suffix = normalizedDescription.slice(separatorIndex + 1).trim();
  if (!suffix || /^future$/i.test(suffix)) return null;

  // Positional descriptions use semicolons between selections, for example:
  // "Code 02: g1 Pos 1 = 1; g4 Pos 4 = A". That final selection is still
  // structured rule content, not the optional free-text suffix.
  if (/\bPos\s+\d+(?:\s*-\s*\d+)?\s*=\s*.+$/i.test(suffix)) return null;

  return suffix;
}

function mapRuleFromApi(apiRule: any): CodeItemsRuleNode | null {
  if (!apiRule) return null;
  
  const appendix = mapAppendixFromApi(apiRule.appendix);
  const normalizedExplicitFreeText = typeof apiRule.freeText === 'string'
    ? apiRule.freeText.trim()
    : '';
  const freeText = normalizedExplicitFreeText || extractEmbeddedFreeText(apiRule.description);
  
  const baseNode = {
    id: apiRule.id || null,
    description: apiRule.description || null,
    appendix,
    freeText,
  };

  const conditionGroup = mapConditionGroupFromApi(apiRule.conditionGroup);
  const consequenceGroups = Array.isArray(apiRule.consequenceGroups)
    ? apiRule.consequenceGroups
      .map((g: any) => mapConditionGroupFromApi(g))
      .filter((g: CodeItemsConditionGroup | null): g is CodeItemsConditionGroup => Boolean(g))
    : null;
  const isFuture = Boolean(apiRule.isFuture);
  
  // Check if it's a While/Loop node (has reference, condition, value but no returnValue)
  if (
    apiRule.reference &&
    apiRule.condition &&
    apiRule.value &&
    !apiRule.returnValue &&
    !apiRule.if &&
    !apiRule.else &&
    !conditionGroup
  ) {
    return {
      ...baseNode,
      type: 'while' as const,
      reference: apiRule.reference,
      condition: apiRule.condition,
      value: apiRule.value
    };
  }
  
  // Check if it's an If node
  if (apiRule.reference || apiRule.returnValue || apiRule.if || apiRule.else || conditionGroup || consequenceGroups?.length || isFuture) {
    return {
      ...baseNode,
      type: 'if' as const,
      reference: apiRule.reference || null,
      condition: apiRule.condition || null,
      value: apiRule.value || null,
      returnValue: apiRule.returnValue || null,
      future: isFuture,
      conditionGroup,
      consequenceGroups,
      if: apiRule.if ? mapRuleFromApi(apiRule.if) as any : null,
      else: apiRule.else ? mapRuleFromApi(apiRule.else) as any : null
    };
  }

  if (baseNode.description || appendix) {
    return {
      ...baseNode,
      type: 'text' as const,
      freeText: baseNode.freeText || baseNode.description || null,
    };
  }

  return null;
}

export function mapCodeItemsToGroups(apiGroups: CodeItemsApiGroup[]): CodeItemsGroup[] {
  return (apiGroups || []).map((g, groupIndex) => {
    // Support both API structures
    // Structure 1: { code, posRange, rows }
    // Structure 2: { codeItem, position, len, formatValues, regions }
    
    let start: number;
    let end: number;
    let code: string;
    
    if (g.posRange && g.code) {
      // Standard structure
      start = Number(g.posRange[0] ?? 1);
      end = Number(g.posRange[1] ?? start);
      code = g.code;
    } else if (g.position !== undefined && g.len !== undefined) {
      // Alternative structure
      start = Number(g.position ?? 1);
      end = start + Number(g.len ?? 1) - 1;
      code = g.codeItem !== undefined ? `C${String(g.codeItem).padStart(2, '0')}` : (g.name || 'C01');
    } else {
      start = 1;
      end = 1;
      code = 'C01';
    }
    
    const label = `${code} [${start}-${end}]`;
    const rawTitle = typeof g.title === 'string'
      ? g.title
      : (typeof g.desc === 'string' ? g.desc : null);
    const groupTitle = rawTitle && rawTitle.trim() ? rawTitle.trim() : null;
    
    // Group-level regions (from alternative structure)
    const groupRegions = Array.isArray(g.regions) ? g.regions : [];
    
    let rows: CodeItemsRow[];
    
    if (Array.isArray(g.rows) && g.rows.length > 0) {
      // Standard structure with rows
      rows = g.rows.map((r, rowIndex) => ({
        id: r.id || null,
        rowId: r.rowId,
        optionCode: r.optionCode ?? '',
        status: normalizeCodeRowStatus(r.status),
        group: typeof r.group === 'string' && r.group.trim() ? r.group.trim() : null,
        valuePosition: Number.isInteger(r.valuePosition) ? Number(r.valuePosition) : null,
        valuePositionEnd: Number.isInteger(r.valuePositionEnd) ? Number(r.valuePositionEnd) : null,
        position: Number.isFinite(r.position) ? Number(r.position) : rowIndex,
        description: r.description ?? null,
        descriptionBlockId: r.descriptionBlockId ?? r.rowId,
        availabilityBlockId: r.availabilityBlockId ?? r.rowId,
        rule: mapRuleFromApi(r.rule),
        appendix: mapAppendixFromApi(r.appendix),
        appendixRefs: normalizeAppendixRefs(r.appendixRefs),
        attributeValues: normalizeAttributeValues(r.attributeValues),
        // Use row-level regions if available, otherwise use group-level regions
        regions: Array.isArray(r.regions) && r.regions.length > 0 ? r.regions : groupRegions,
      }));
    } else if (Array.isArray(g.formatValues) && g.formatValues.length > 0) {
      // Alternative structure with formatValues
      rows = g.formatValues.map((fv, idx) => {
        const rowId = fv.id || `${code}-${idx}`;
        return {
          id: fv.id || null,
          rowId,
          optionCode: fv.value ?? fv.name ?? '',
          status: normalizeCodeRowStatus(fv.status),
          group: typeof fv.group === 'string' && fv.group.trim() ? fv.group.trim() : null,
          valuePosition: Number.isInteger(fv.valuePosition) ? Number(fv.valuePosition) : null,
          valuePositionEnd: Number.isInteger(fv.valuePositionEnd) ? Number(fv.valuePositionEnd) : null,
          position: Number.isFinite(fv.position) ? Number(fv.position) : idx,
          description: fv.desc ?? null,
          descriptionBlockId: fv.descriptionBlockId ?? rowId,
          availabilityBlockId: fv.availabilityBlockId ?? rowId,
          rule: mapRuleFromApi(fv.rule),
          appendix: mapAppendixFromApi((fv as any).appendix),
          appendixRefs: normalizeAppendixRefs(fv.appendixRefs),
          attributeValues: normalizeAttributeValues(fv.attributeValues),
          // Use formatValue-level regions if available, otherwise use group-level regions
          regions: Array.isArray(fv.regions) && fv.regions.length > 0 ? fv.regions : groupRegions,
        };
      });
    } else {
      rows = [];
    }
    
    const isComposite = rows.some(row => Number.isInteger(row.valuePosition));

    return {
      id: g.id || null,
      localKey: makeCodeItemLocalKey(code, start, g.id, groupIndex),
      codeItemPos: { code, posRange: [start, end], label, isDefault: !!g.isDefault },
      title: groupTitle,
      attributeSchemas: normalizeAttributeSchemaScopes(g.attributeSchemas),
      authoringMode: isComposite ? 'composite' as const : 'simple' as const,
      subgroups: isComposite ? deriveCompositeSubgroups(rows as any) : [],
      ...(g.appendix ? { appendix: mapAppendixFromApi(g.appendix) } : {}),
      appendixRefs: normalizeAppendixRefs(g.appendixRefs),
      groupAppendixRefs: normalizeGroupAppendixRefs(g.groupAppendixRefs),
      rows,
    };
  });
}

export async function fetchCodeIndexCodeItems(formatId: string, projectId?: string | null): Promise<CodeItemsGroup[]> {
  // Important: path without '/api' prefix; baseURL should already include it when needed
  const path = `/CodeIndexes/${encodeURIComponent(formatId)}/codeitems`;
  const { data } = projectId
    ? await axios.get<CodeItemsApiGroup[]>(path, { params: { projectId } })
    : await axios.get<CodeItemsApiGroup[]>(path);
  return mapCodeItemsToGroups(Array.isArray(data) ? data : []);
}

// -----------------------------
// Code Index - Submit Version (Details)
// -----------------------------

export type SubmitChangeType = 'Create' | 'Update' | 'Delete';

export interface SubmitChangeItem {
  changeType: SubmitChangeType;
  nodeType: string;
  nodeId: string | null;
  path: string | null;
  oldValue: string | null;
  newValue: string | null;
}

export interface SubmitVersionRequest {
  description: string;
  isMajor: boolean;
  changes: SubmitChangeItem[];
}

export interface SubmitVersionResponse {
  id: string;
  description: string;
  status: string;
  isMajor: boolean;
  changes: Array<{
    changeType: string;
    nodeType: string;
    nodeId: string;
    path: string | null;
    oldValue: string | null;
    newValue: string | null;
    approvalStatus: string;
    id: string;
    createdAt: string | null;
  }>;
  createdBy: {
    id: string;
    name: string;
    emailAddress: string;
    avatarUri: string | null;
  } | null;
  createdAt: string | null;
}

export async function submitCodeIndexVersion(
  codeIndexId: string,
  payload: SubmitVersionRequest
): Promise<SubmitVersionResponse> {
  const submitPayload = {
    ...payload,
    changes: payload.changes.map(change => ({
      ...change,
      oldValue: sanitizeSubmitChangeValue(change.oldValue),
      newValue: sanitizeSubmitChangeValue(change.newValue),
    })),
  };

  const { data } = await axios.post<SubmitVersionResponse>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/versions/submit`,
    submitPayload,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
}

export interface RollbackVersionRequest {
  targetVersionId: string;
  fromVersionId: string | null;
  description?: string | null;
}

export async function rollbackCodeIndexVersion(
  codeIndexId: string,
  payload: RollbackVersionRequest
): Promise<unknown> {
  const { data } = await axios.post(
    `/codeindex/${encodeURIComponent(codeIndexId)}/versions/rollback`,
    payload,
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
}

function getCodeItemGroupIdentity(group: CodeItemsGroup): string {
  const normalizedId = typeof group.id === 'string' ? group.id.trim() : '';
  if (normalizedId) return `id:${normalizedId}`;

  const normalizedLocalKey = typeof group.localKey === 'string' ? group.localKey.trim() : '';
  if (normalizedLocalKey) return `localKey:${normalizedLocalKey}`;

  return `code:${group.codeItemPos.code}`;
}

function buildCodeItemChangeSnapshot(group: CodeItemsGroup) {
  return {
    code: group.codeItemPos.code,
    posRange: group.codeItemPos.posRange,
    isDefault: group.codeItemPos.isDefault,
    title: group.title ?? null,
    attributeSchemas: normalizeAttributeSchemaScopes(group.attributeSchemas),
    appendixRefs: normalizeAppendixRefs(group.appendixRefs),
    groupAppendixRefs: normalizeGroupAppendixRefs(group.groupAppendixRefs),
    rows: group.rows.map(buildSubmitCodeRowSnapshot),
  };
}

function normalizeSubmitStatusValue(value: string | null | undefined): string {
  const normalizedValue = String(value ?? '').trim().toUpperCase();
  if (!normalizedValue || normalizedValue === 'EMPTY') return 'Empty';
  return normalizedValue;
}

function submitChangeJsonReplacer(_key: string, value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;

  const maybeCondition = value as Partial<CodeItemsConditionEntry>;
  const hasRangeProperties = 'from' in value || 'to' in value;
  const isBetween = String(maybeCondition.condition ?? '').trim().toLowerCase() === 'between';
  if (!hasRangeProperties || isBetween) {
    return value;
  }

  const { from, to, ...rest } = value as Record<string, unknown>;
  return rest;
}

function stringifySubmitChangeValue(value: unknown): string {
  return JSON.stringify(value, submitChangeJsonReplacer);
}

function sanitizeSubmitChangeValue(value: string | null): string | null {
  if (value === null) return null;

  try {
    return stringifySubmitChangeValue(JSON.parse(value));
  } catch {
    return value;
  }
}

function buildSubmitCodeRowSnapshot(row: CodeItemsRow): CodeItemsRow {
  return {
    ...row,
    status: normalizeSubmitStatusValue(row.status),
    attributeValues: normalizeAttributeValues(row.attributeValues),
    appendixRefs: normalizeAppendixRefs(row.appendixRefs),
  };
}

function resolveFormatValueNodeId(
  originalRow: CodeItemsRow | undefined,
  currentRow: CodeItemsRow | undefined,
  rowId: string
): string {
  return originalRow?.id ?? currentRow?.id ?? rowId;
}

function resolveComparableRowKey(row: CodeItemsRow): string {
  return String(row.id ?? row.rowId ?? '').trim();
}

function resolveFormatValueFallbackRowId(
  originalRow: CodeItemsRow | undefined,
  currentRow: CodeItemsRow | undefined,
  rowKey: string
): string {
  return String(currentRow?.rowId ?? originalRow?.rowId ?? rowKey).trim();
}

function resolveCodeItemNodeId(
  originalGroup: CodeItemsGroup | undefined,
  currentGroup: CodeItemsGroup | undefined
): string {
  return originalGroup?.id
    ?? currentGroup?.id
    ?? originalGroup?.codeItemPos.code
    ?? currentGroup?.codeItemPos.code
    ?? '';
}

function resolveCodeItemPosition(group: CodeItemsGroup): number {
  return Number(group.codeItemPos.posRange?.[0] ?? 0);
}

function resolveCodeItemLen(group: CodeItemsGroup): number {
  const start = resolveCodeItemPosition(group);
  const end = Number(group.codeItemPos.posRange?.[1] ?? start);
  return Math.max(0, end - start);
}

// Diff utility: computes granular changes between original and current groups
export function computeCodeItemChanges(
  originalGroups: CodeItemsGroup[],
  currentGroups: CodeItemsGroup[]
): SubmitChangeItem[] {
  const changes: SubmitChangeItem[] = [];

  const origMap = new Map<string, CodeItemsGroup>();
  for (const g of originalGroups) {
    origMap.set(getCodeItemGroupIdentity(g), g);
  }

  const currMap = new Map<string, CodeItemsGroup>();
  for (const g of currentGroups) {
    currMap.set(getCodeItemGroupIdentity(g), g);
  }

  // Deleted groups
  origMap.forEach((origGroup, groupIdentity) => {
    if (!currMap.has(groupIdentity)) {
      changes.push({
        changeType: 'Delete',
        nodeType: 'CodeItem',
        nodeId: resolveCodeItemNodeId(origGroup, undefined),
        path: null,
        oldValue: stringifySubmitChangeValue(buildCodeItemChangeSnapshot(origGroup)),
        newValue: null,
      });
    }
  });

  // New and updated groups
  currMap.forEach((currGroup, groupIdentity) => {
    const origGroup = origMap.get(groupIdentity);

    if (!origGroup) {
      changes.push({
        changeType: 'Create',
        nodeType: 'CodeItem',
        nodeId: null,
        path: null,
        oldValue: null,
        newValue: stringifySubmitChangeValue(buildCodeItemChangeSnapshot(currGroup)),
      });
      return;
    }

    const codeItemNodeId = resolveCodeItemNodeId(origGroup, currGroup);
    const originalCode = String(origGroup.codeItemPos.code ?? '').trim();
    const currentCode = String(currGroup.codeItemPos.code ?? '').trim();
    if (originalCode !== currentCode) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'name',
        oldValue: stringifySubmitChangeValue(originalCode),
        newValue: stringifySubmitChangeValue(currentCode),
      });
    }

    const originalPosition = resolveCodeItemPosition(origGroup);
    const currentPosition = resolveCodeItemPosition(currGroup);
    if (originalPosition !== currentPosition) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'position',
        oldValue: stringifySubmitChangeValue(originalPosition),
        newValue: stringifySubmitChangeValue(currentPosition),
      });
    }

    const originalLen = resolveCodeItemLen(origGroup);
    const currentLen = resolveCodeItemLen(currGroup);
    if (originalLen !== currentLen) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'len',
        oldValue: stringifySubmitChangeValue(originalLen),
        newValue: stringifySubmitChangeValue(currentLen),
      });
    }

    if ((origGroup.title ?? null) !== (currGroup.title ?? null)) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'title',
        oldValue: stringifySubmitChangeValue(origGroup.title ?? null),
        newValue: stringifySubmitChangeValue(currGroup.title ?? null),
      });
    }

    const origAttributeSchemas = stringifySubmitChangeValue(normalizeAttributeSchemaScopes(origGroup.attributeSchemas));
    const currAttributeSchemas = stringifySubmitChangeValue(normalizeAttributeSchemaScopes(currGroup.attributeSchemas));
    if (origAttributeSchemas !== currAttributeSchemas) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'attributeSchemas',
        oldValue: origAttributeSchemas,
        newValue: currAttributeSchemas,
      });
    }

    const origAppendixRefs = stringifySubmitChangeValue(normalizeAppendixRefs(origGroup.appendixRefs));
    const currAppendixRefs = stringifySubmitChangeValue(normalizeAppendixRefs(currGroup.appendixRefs));
    if (origAppendixRefs !== currAppendixRefs) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'appendixRefs',
        oldValue: origAppendixRefs,
        newValue: currAppendixRefs,
      });
    }

    const origGroupAppendixRefs = stringifySubmitChangeValue(normalizeGroupAppendixRefs(origGroup.groupAppendixRefs));
    const currGroupAppendixRefs = stringifySubmitChangeValue(normalizeGroupAppendixRefs(currGroup.groupAppendixRefs));
    if (origGroupAppendixRefs !== currGroupAppendixRefs) {
      changes.push({
        changeType: 'Update',
        nodeType: 'CodeItem',
        nodeId: codeItemNodeId,
        path: 'groupAppendixRefs',
        oldValue: origGroupAppendixRefs,
        newValue: currGroupAppendixRefs,
      });
    }

    const origRowMap = new Map<string, CodeItemsRow>();
    origGroup.rows.forEach((row) => {
      origRowMap.set(resolveComparableRowKey(row), row);
    });

    const currRowMap = new Map<string, CodeItemsRow>();
    currGroup.rows.forEach((row) => {
      currRowMap.set(resolveComparableRowKey(row), row);
    });

    // Deleted rows
    origRowMap.forEach((origRow, rowKey) => {
      if (!currRowMap.has(rowKey)) {
        changes.push({
          changeType: 'Delete',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(
            origRow,
            undefined,
            resolveFormatValueFallbackRowId(origRow, undefined, rowKey)
          ),
          path: 'optionCode',
          oldValue: stringifySubmitChangeValue(buildSubmitCodeRowSnapshot(origRow)),
          newValue: null,
        });
      }
    });

    // New and updated rows
    currRowMap.forEach((currRow, rowKey) => {
      const origRow = origRowMap.get(rowKey);
      const fallbackRowId = resolveFormatValueFallbackRowId(origRow, currRow, rowKey);

      if (!origRow || currRow.isNew) {
        changes.push({
          changeType: 'Create',
          nodeType: 'FormatValue',
          nodeId: null,
          path: currGroup.codeItemPos.code,
          oldValue: null,
          newValue: stringifySubmitChangeValue(buildSubmitCodeRowSnapshot(currRow)),
        });
        return;
      }

      if (origRow.optionCode !== currRow.optionCode) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'optionCode',
          oldValue: stringifySubmitChangeValue(origRow.optionCode),
          newValue: stringifySubmitChangeValue(currRow.optionCode),
        });
      }

      const positionedScalarChanges: Array<{
        path: 'group' | 'position' | 'valuePosition' | 'valuePositionEnd';
        oldValue: string | number | null | undefined;
        newValue: string | number | null | undefined;
      }> = [
        { path: 'group', oldValue: origRow.group ?? null, newValue: currRow.group ?? null },
        { path: 'position', oldValue: origRow.position, newValue: currRow.position },
        { path: 'valuePosition', oldValue: origRow.valuePosition ?? null, newValue: currRow.valuePosition ?? null },
        { path: 'valuePositionEnd', oldValue: origRow.valuePositionEnd ?? null, newValue: currRow.valuePositionEnd ?? null },
      ];

      positionedScalarChanges.forEach((scalarChange) => {
        if (scalarChange.oldValue === scalarChange.newValue) return;
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: scalarChange.path,
          oldValue: stringifySubmitChangeValue(scalarChange.oldValue),
          newValue: stringifySubmitChangeValue(scalarChange.newValue),
        });
      });

      if (origRow.description !== currRow.description) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'description',
          oldValue: stringifySubmitChangeValue(origRow.description),
          newValue: stringifySubmitChangeValue(currRow.description),
        });
      }

      if ((origRow.status ?? '') !== (currRow.status ?? '')) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'status',
          oldValue: stringifySubmitChangeValue(normalizeSubmitStatusValue(origRow.status)),
          newValue: stringifySubmitChangeValue(normalizeSubmitStatusValue(currRow.status)),
        });
      }

      const origRegions = stringifySubmitChangeValue(origRow.regions ?? []);
      const currRegions = stringifySubmitChangeValue(currRow.regions ?? []);
      if (origRegions !== currRegions) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'regions',
          oldValue: origRegions,
          newValue: currRegions,
        });
      }

      const origRowAppendixRefs = stringifySubmitChangeValue(normalizeAppendixRefs(origRow.appendixRefs));
      const currRowAppendixRefs = stringifySubmitChangeValue(normalizeAppendixRefs(currRow.appendixRefs));
      if (origRowAppendixRefs !== currRowAppendixRefs) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'appendixRefs',
          oldValue: origRowAppendixRefs,
          newValue: currRowAppendixRefs,
        });
      }

      const origRule = stringifySubmitChangeValue(origRow.rule ?? null);
      const currRule = stringifySubmitChangeValue(currRow.rule ?? null);
      if (origRule !== currRule) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'rule',
          oldValue: origRule,
          newValue: currRule,
        });
      }

      const origAttributeValues = stringifySubmitChangeValue(normalizeAttributeValues(origRow.attributeValues));
      const currAttributeValues = stringifySubmitChangeValue(normalizeAttributeValues(currRow.attributeValues));
      if (origAttributeValues !== currAttributeValues) {
        changes.push({
          changeType: 'Update',
          nodeType: 'FormatValue',
          nodeId: resolveFormatValueNodeId(origRow, currRow, fallbackRowId),
          path: 'attributeValues',
          oldValue: origAttributeValues,
          newValue: currAttributeValues,
        });
      }
    });
  });

  return changes;
}

// -----------------------------
// Code Sheet - Publish (Details)
// -----------------------------

export interface CodeItemsPublishAppendix {
  id: string | null;
  title: string | null;
  description: string | null;
  content: string | null;
  attachment: string | null;
  entityId: string | null;
  parentEntityId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  tables?: unknown[] | null;
}

export interface CodeItemsPublishRule {
  id: string | null;
  description: string | null;
  appendix: CodeItemsPublishAppendix | null;
  reference?: string | null;
  condition?: string | null;
  value?: string | null;
  returnValue?: string | null;
  isFuture?: boolean | null;
  conditionGroup?: CodeItemsConditionGroup | null;
  consequenceGroups?: CodeItemsConditionGroup[] | null;
  if?: CodeItemsPublishRule | null;
  else?: CodeItemsPublishRule | null;
}

export interface CodeItemsPublishRow {
  id: string | null;
  rowId: string;
  optionCode: string;
  status: string;
  group?: string | null;
  valuePosition: number | null;
  valuePositionEnd: number | null;
  position: number;
  description: string | null;
  descriptionBlockId: string;
  availability: null;
  availabilityBlockId: string | null;
  appendix: CodeItemsPublishAppendix | null;
  rule: CodeItemsPublishRule | null;
  attributeValues: Record<string, string | null>;
  appendixRefs: string[];
  regions?: string[];
}

export interface CodeItemsPublishGroup {
  id: string | null;
  code: string;
  posRange: number[];
  isDefault: boolean;
  title: string | null;
  attributeSchemas: CodeItemAttributeSchemaScope[];
  appendixRefs: string[];
  groupAppendixRefs: Record<string, string[]>;
  rows: CodeItemsPublishRow[];
  regions?: string[];
}

function buildAppendixPayload(appendix: Appendix | null | undefined): CodeItemsPublishAppendix | null {
  if (!appendix) return null;

  return {
    id: appendix.id || null,
    title: appendix.title || null,
    description: appendix.description || null,
    content: appendix.content || null,
    attachment: appendix.attachment || null,
    entityId: appendix.entityId || null,
    parentEntityId: appendix.parentEntityId || null,
    createdAt: appendix.createdAt || null,
    updatedAt: appendix.updatedAt || null,
    ...(Array.isArray(appendix.tables) ? { tables: appendix.tables } : {}),
  };
}

function buildConditionGroupPayload(group: any): any {
  if (!group) return null;

  return {
    logicalOperator: group.logicalOperator || null,
    conditions: Array.isArray(group.conditions)
      ? group.conditions.map((condition: any) => {
          const baseCondition = {
            reference: condition.reference || null,
            condition: condition.condition || null,
          };
          if (condition.values && typeof condition.values === 'object' && !Array.isArray(condition.values)) {
            return {
              ...baseCondition,
              values: Object.fromEntries(
                Object.entries(condition.values)
                  .filter(([key, value]) => String(key).trim() && String(value ?? '').trim())
                  .map(([key, value]) => [String(key), String(value).toUpperCase()])
              ),
            };
          }
          if (String(condition.condition ?? '').trim().toLowerCase() === 'between') {
            return {
              ...baseCondition,
              value: null,
              from: condition.from || null,
              to: condition.to || null,
            };
          }
          return {
            ...baseCondition,
            value: condition.value || null,
          };
        })
      : null,
    groups: Array.isArray(group.groups)
      ? group.groups.map((subgroup: any) => buildConditionGroupPayload(subgroup))
      : null,
  };
}

function buildRulePayload(rule: RuleNode | null | undefined, description?: string | null): CodeItemsPublishRule | null {
  if (!rule) return null;

  const basePayload: CodeItemsPublishRule = {
    id: rule.id || null,
    description: description || rule.description || null,
    appendix: buildAppendixPayload(rule.appendix),
  };

  if (rule.type === 'while') {
    return {
      ...basePayload,
      reference: rule.reference || null,
      condition: rule.condition || null,
      value: rule.value || null,
    };
  }

  if (rule.type === 'if') {
    const ifRule = rule as IfNode;
    return {
      ...basePayload,
      reference: ifRule.reference || null,
      condition: ifRule.condition || null,
      value: ifRule.value || null,
      returnValue: ifRule.returnValue || null,
      isFuture: Boolean(ifRule.future),
      conditionGroup: ifRule.conditionGroup ? buildConditionGroupPayload(ifRule.conditionGroup) : null,
      consequenceGroups: Array.isArray(ifRule.consequenceGroups)
        ? ifRule.consequenceGroups.map(group => buildConditionGroupPayload(group))
        : null,
      if: ifRule.if ? buildRulePayload(ifRule.if) : null,
      else: ifRule.else ? buildRulePayload(ifRule.else) : null,
    };
  }

  return basePayload;
}

export function buildCodeItemsPublishPayload(groups: CodeItemGroup[]): CodeItemsPublishGroup[] {
  return groups.map(group => {
    const [start, end] = group.codeItemPos.posRange;

    return {
      id: group.id || null,
      code: group.codeItemPos.code,
      posRange: [start, end],
      isDefault: false,
      title: group.title ?? null,
      attributeSchemas: normalizeAttributeSchemaScopes(group.attributeSchemas),
      appendixRefs: normalizeAppendixRefs(group.appendixRefs),
      groupAppendixRefs: normalizeGroupAppendixRefs(group.groupAppendixRefs),
      rows: group.rows.map((row, index) => ({
        id: row.id || null,
        rowId: row.rowId,
        optionCode: row.optionCode || '',
        status: normalizeCodeRowStatus(row.status) || 'Empty',
        group: row.group ?? null,
        valuePosition: Number.isInteger(row.valuePosition) ? Number(row.valuePosition) : null,
        valuePositionEnd: Number.isInteger(row.valuePositionEnd) ? Number(row.valuePositionEnd) : null,
        position: Number.isFinite(row.position) ? row.position : index,
        description: row.description ?? null,
        descriptionBlockId: row.descriptionBlockId || row.rowId,
        availability: null,
        availabilityBlockId: row.availabilityBlockId || row.rowId,
        appendix: buildAppendixPayload(row.appendix),
        rule: row.rule ? buildRulePayload(row.rule, row.rule.description) : null,
        attributeValues: normalizeAttributeValues(row.attributeValues),
        appendixRefs: normalizeAppendixRefs(row.appendixRefs),
        regions: row.regions || [],
      })),
    };
  });
}

export interface PublishFormatResponse {
  success?: boolean;
  message?: string | null;
  // Present when the save targeted a project working copy; null when it targeted main.
  unselectableValues?: UnselectableValuesReport | null;
}

export async function publishCodeIndexCodeItems(
  formatId: string,
  payload: CodeItemsPublishGroup[],
  projectId?: string | null
): Promise<PublishFormatResponse> {
  const { data } = await axios.post<PublishFormatResponse>(
    `/CodeIndexes/${encodeURIComponent(formatId)}/codeitems/publish`,
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
      ...(projectId ? { params: { projectId } } : {}),
    }
  );
  return data;
}

// -----------------------------
// Code Sheet - Header (Details)
// -----------------------------
export interface CodeIndexHeader {
  partNumber: string;
  businessUnit: string;
  productLine: string;
  version?: string;
  lastMergedText: string;
  format_Id?: string;
}

function mapCodeIndexHeader(data: CodeIndexApiItem | null | undefined): CodeIndexHeader {
  const lastMerged = data?.lastMerged ? String(data.lastMerged) : null;
  const hasLastMerged = Boolean(lastMerged && !lastMerged.startsWith('0001-01-01'));
  const normalizedLastMerged = lastMerged && !/(?:Z|[+-]\d{2}:\d{2})$/i.test(lastMerged)
    ? `${lastMerged}Z`
    : lastMerged;

  return {
    partNumber: data?.partNumber || '',
    businessUnit: data?.businessUnit || '',
    productLine: data?.productLine || '',
    version: data?.publishedVersion?.semVer || '--',
    lastMergedText: hasLastMerged ? formatDateTime(normalizedLastMerged) : '--',
    format_Id: data?.format_Id || '',
  };
}

export async function fetchCodeIndexHeader(id: string): Promise<CodeIndexHeader> {
  const { data } = await axios.get<CodeIndexApiItem>(`/CodeIndexes/${encodeURIComponent(id)}`);
  return mapCodeIndexHeader(data);
}

export interface CodeIndexVersion {
  semVer?: string;
  state?: number;
  isMajor?: boolean;
  description?: string | null;
  previousVersion?: CodeIndexVersion | null;
  treeNodes?: any | null;
  id?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CodeIndexApiItem {
  name?: string;
  format_Id?: string;
  partNumber?: string;
  productFamily?: string;
  productLine?: string;
  pcl?: string;
  creator?: string; // deprecated: use createdBy
  stackMembers?: string[]; // deprecated: use stacked
  description?: string;
  codeItemsCount?: number;
  codeStringLength?: number;
  id?: string;
  createdAt?: string;
  updatedAt?: string | null;
  lastMerged?: string | null;
  image?: string | null;
  businessUnit?: string;
  userId?: string;
  attachments?: string[] | null;
  createdBy?: {
    userId?: string;
    name?: string;
    emailAddress?: string;
    avatarUri?: string | null;
    id?: string;
    createdAt?: string;
    updatedAt?: string | null;
  } | null;
  stacked?: Array<{
    userId?: string;
    name?: string;
    emailAddress?: string;
    avatarUri?: string | null;
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
  }> | null;
  relatedPcl?: Array<{
    value?: string;
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
  }> | null;
  codeItems?: Array<{
    codeItem?: number;
    position?: number;
    len?: number;
    name?: string;
    mode?: string;
    title?: string | null;
    desc?: string;
    regions?: string[];
    formatId?: string | null;
    formatValues?: Array<{
      name?: string;
      value?: string;
      desc?: string;
      descriptionBlockId?: string;
      availabilityBlockId?: string | null;
      codeItemId?: string | null;
      regions?: string[];
      rule?: {
        description?: string | null;
        appendix?: {
          title?: string;
          description?: string;
          content?: string;
          id?: string;
        } | null;
        id?: string;
      } | null;
      id?: string;
    }>;
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
  }> | null;
  versions?: CodeIndexVersion[] | null;
  publishedVersion?: CodeIndexVersion | null;
}

export interface CodeIndexDetailsSnapshot {
  codeIndexId: string;
  header: CodeIndexHeader;
  groups: CodeItemsGroup[];
  versionId: string;
  semVer: string;
}

export interface CodeIndexApiResponse {
  results: CodeIndexApiItem[];
  totalCount: number;
  totalResults?: number;
}

export interface CodeIndexListResult {
  items: TableRowItem[];
  totalCount: number;
  totalResults: number;
}

export interface TableRowItem {
  partNumber: string;
  productLine: string;
  revision: string;
  version?: string;
  format: string;
  pcl: string;
  description: string;
  lastModification: string;
  lastModificationRaw: string;
  creator: string;
  productFamily: string;
  stackMembers: string[];
  stackedUsers?: Array<{ name?: string; avatarUri?: string | null }>;
  image?: string | null;
  id?: string;
  name?: string;
  businessUnit?: string;
  userId?: string;
  codeItemsCount?: number;
  codeStringLength?: number;
  attachments?: string[];
  versions?: CodeIndexVersion[];
  publishedVersion?: CodeIndexVersion | null;
}

export interface CodeIndexQueryParams {
  keyword?: string | null;
  productFamily?: string | null;
  productLine?: string | null;
  users?: string[] | null;
  pcls?: string[] | null;
  businessUnit?: string | null;
  page?: number | null;
  pageSize?: number | null;
  skip?: number | null;
  orderByField?: string | null;
  descending?: boolean | null;
}

export async function fetchCodeIndexes(params?: CodeIndexQueryParams): Promise<CodeIndexListResult> {
  const query: Record<string, any> = {};
  if (params?.keyword) query.TextSearch = params.keyword;
  if (params?.productFamily) query.ProductFamily = params.productFamily;
  if (params?.productLine) query.ProductLine = params.productLine;
  if (params?.users && params.users.length) query.Users = params.users;
  if (params?.pcls && params.pcls.length) query.Pcls = params.pcls;
  if (params?.businessUnit) query.BusinessUnit = params.businessUnit;
  if (params?.page != null) query.Page = params.page;
  if (params?.pageSize != null) query.PageSize = params.pageSize;
  if (params?.skip != null) query.Skip = params.skip;
  if (params?.orderByField) query.OrderByField = params.orderByField;
  if (params?.descending != null) query.Descending = params.descending;

  // Ensure arrays serialize as repeated keys: Users=a&Users=b (OpenAPI form, explode=true)
  const { data } = await axios.get<CodeIndexApiResponse>(`/CodeIndexes`, {
    params: query,
    paramsSerializer: {
      serialize: (p: Record<string, any>) => {
        const usp = new URLSearchParams();
        Object.entries(p).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => {
              if (v !== undefined && v !== null) usp.append(key, String(v));
            });
          } else if (value !== undefined && value !== null) {
            usp.append(key, String(value));
          }
        });
        return usp.toString();
      }
    }
  });
  const items = Array.isArray(data?.results) ? data.results : [];

  const mapped: TableRowItem[] = items.map((it): TableRowItem => {
    const updatedAt = it.updatedAt ? String(it.updatedAt) : null;
    const isUpdatedSentinel = updatedAt ? updatedAt.startsWith('0001-01-01') : false;
    const hasRealUpdate = Boolean(updatedAt && !isUpdatedSentinel);
    const lastModText = hasRealUpdate ? formatDateTime(updatedAt) : 'Never updated';
    const lastModRaw = hasRealUpdate ? updatedAt as string : String(it.createdAt || '');

    return {
    partNumber: it.partNumber || '',
    productLine: it.productLine || '',
    revision: '',
    version: '',
    format: it.format_Id || '',
    pcl: it.pcl || '',
    description: it.description || '',
    lastModification: lastModText,
    lastModificationRaw: lastModRaw,
    creator: (it.createdBy?.name && it.createdBy.name.trim())
      ? it.createdBy.name
      : (it.creator && it.creator.trim())
        ? it.creator
        : 'Unknown',
    productFamily: it.productFamily || '',
    stackMembers: Array.isArray(it.stacked)
      ? it.stacked.map(m => m?.name).filter((n): n is string => Boolean(n && n.trim()))
      : Array.isArray(it.stackMembers)
        ? it.stackMembers.filter(Boolean)
        : [],
      stackedUsers: Array.isArray(it.stacked)
        ? it.stacked.map(m => ({ name: m?.name, avatarUri: m?.avatarUri ?? null }))
        : undefined,
    image: it.image ?? null,
    id: it.id,
    name: it.name,
    businessUnit: it.businessUnit,
    userId: it.createdBy?.userId || it.userId,
    codeItemsCount: it.codeItemsCount,
    codeStringLength: it.codeStringLength,
    attachments: Array.isArray((it as any).attachments) ? (it as any).attachments.filter(Boolean) as string[] : [],
    versions: Array.isArray(it.versions) ? it.versions : undefined,
    publishedVersion: it.publishedVersion ?? null,
  };
  });

  return {
    items: mapped,
    totalCount: Number(data?.totalCount || mapped.length),
    totalResults: Number((data as any)?.totalResults ?? mapped.length),
  };
}

// -----------------------------
// Code Sheet - Versions Changelog
// -----------------------------

export interface VersionAuthor {
  id: string;
  name: string;
  emailAddress: string;
  avatarUri: string | null;
}

export interface ChangeEntry {
  id: string;
  changeType: string;
  nodeType: string;
  path: string | null;
  oldValue: string | null;
  newValue: string | null;
  approvalStatus: string;
}

export interface VersionChangeSet {
  id: string;
  description: string;
  status: string;
  createdBy: VersionAuthor;
  createdAt: string;
}

export interface VersionEntry {
  versionId: string;
  semVer: string;
  state: string;
  isMajor: boolean;
  isCurrentVersion: boolean;
  description: string | null;
  createdAt: string;
  previousVersionId: string | null;
  changesCount: number;
  changeSet: VersionChangeSet;
  changes: ChangeEntry[];
}

export interface VersionsChangelogResponse {
  codeIndexId: string;
  codeIndexName: string;
  currentVersionId: string | null;
  currentVersion: VersionEntry | null;
  totalVersions: number;
  versions: VersionEntry[];
}

export async function fetchCodeIndexVersionsChangelog(codeIndexId: string): Promise<VersionsChangelogResponse> {
  const { data } = await axios.get<VersionsChangelogResponse>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/versions/changelog`
  );
  return data;
}

export interface CodeIndexVersionDetailsResponse {
  id?: string;
  semVer?: string;
  state?: number | string;
  isMajor?: boolean;
  description?: string | null;
  previousVersion?: CodeIndexVersion | null;
  treeNodes?: CodeItemsApiGroup[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function fetchCodeIndexDetailsSnapshot(id: string): Promise<CodeIndexDetailsSnapshot> {
  const [header, groups] = await Promise.all([
    fetchCodeIndexHeader(id),
    fetchCodeIndexCodeItems(id),
  ]);

  return {
    codeIndexId: String(id),
    header,
    groups,
    versionId: String(id),
    semVer: String(header.version || ''),
  };
}

// --------------------------------
// Duplicate Code Index
// --------------------------------
export async function duplicateCodeIndex(id: string, format: string, showLoading?: boolean): Promise<string | null> {
  const confirmed = await showConfirmModal({
    title: 'Duplicate Code Index',
    description: `Are you sure to duplicate the Code Index with Format ${format}?`,
  });
  if (!confirmed) return null;
  if (showLoading) showLoadingModal({ title: 'Duplicating Code Sheet', message: 'Please wait while Code Sheet is duplicated...' });
  try {
    const { data } = await axios.post(`/CodeIndexes/${encodeURIComponent(id)}/copy`);
    if (showLoading) closeLoadingModal();
    await showSuccessModal({
      title: 'Copied',
      description: getApiMessage(data) || `The Code Index with Format ${format} has been duplicated.`,
      timer: 1200,
      showConfirmButton: false
    });
    return data?.id ?? null;
  } catch (error) {
    if (showLoading) closeLoadingModal();
    await showErrorModal({ title: 'Error', description: getApiMessage(error) || 'Action failed. Please try again.' });
    return null;
  }
}


